import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import generateColours from '../generateColours';
import TotalCategoryResponse from '../../../types/Analytics/TotalCategoryResponse';

interface TopicsSharedLegendProps {
  topicsData?: TotalCategoryResponse;
  selectedGroups: string[];
  onToggleGroup: (group: string) => void;
}

const INITIAL_DISPLAY_COUNT = 5;

function TopicsSharedLegend({
  topicsData,
  selectedGroups,
  onToggleGroup
}: Readonly<TopicsSharedLegendProps>): React.ReactElement | null {
  const [expandedMode, setExpandedMode] = useState(false);

  const legendItems = useMemo(() => {
    if (!topicsData?.categories || topicsData.categories.length === 0) {
      return [];
    }

    const filteredCategories = topicsData.categories.filter(
      (cat) => cat.category.toLowerCase() !== 'missed utterance'
    );
    const sortedCategories = [...filteredCategories].sort(
      (a, b) => b.count - a.count
    );
    const colors = generateColours(topicsData.categories.length);

    return sortedCategories.map((cat) => {
      const originalIndex = topicsData.categories.findIndex(
        (c) => c.category === cat.category
      );
      return {
        label: cat.category,
        color: colors[originalIndex],
        count: cat.count
      };
    });
  }, [topicsData]);

  const displayedItems = expandedMode
    ? legendItems
    : legendItems.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMoreItems = legendItems.length > INITIAL_DISPLAY_COUNT;

  return (
    <div className='flex flex-col items-center gap-3 rounded bg-white p-4 min-h-[72px]'>
      <div
        className='flex flex-wrap justify-center gap-4'
        style={{
          maxHeight: expandedMode ? '285px' : 'auto',
          overflowY: expandedMode ? 'auto' : 'visible',
          width: '100%'
        }}
      >
        {displayedItems.map((item) => {
          const isSelected = selectedGroups.includes(item.label);
          return (
            <button
              key={item.label}
              onClick={() => onToggleGroup(item.label)}
              type='button'
              className='flex items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-gray-100'
              style={{
                opacity: isSelected ? 1 : 0.5
              }}
            >
              <div
                className='h-4 w-4 rounded-full'
                style={{ backgroundColor: item.color }}
              />
              <span className='text-sm text-gray-700'>{item.label}</span>
            </button>
          );
        })}
      </div>
      {hasMoreItems && (
        <button
          onClick={() => setExpandedMode(!expandedMode)}
          type='button'
          className='flex items-center gap-1 text-xs text-blue-600 transition-colors hover:text-blue-800'
        >
          {expandedMode ? (
            <>
              <span>Show less</span>
              <ChevronUp size={14} />
            </>
          ) : (
            <>
              <span>Show more</span>
              <ChevronDown size={14} />
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default TopicsSharedLegend;
